import React, { FC, useCallback, useEffect, useState } from "react";

import { Container } from "../Container/Container";
import { Button } from "../Button/Button";
import AIIcon from "../../assets/heroicons/aiIcon.svg";
import styles from "./Hero.module.scss";

const usages = [
	"training",
	"education",
	"marketing",
	"events",
	"assessment",
] as const;

const typingPause = 75,
	fullPause = 2000,
	emptyPause = 1000;

interface HeroState {
	usageIndex: number;
	text: string;
	decreasing: boolean;
	timeout: ReturnType<typeof setTimeout> | null;
}

export const Hero: FC = () => {
	const [current, setCurrent] = useState<HeroState>({
		usageIndex: 0,
		text: usages[0],
		decreasing: true,
		timeout: null,
	});

	const looper = useCallback(() => {
		setCurrent((current) => {
			const { usageIndex, text, decreasing } = current,
				newIndex =
					text !== ""
						? usageIndex
						: usageIndex === usages.length - 1
						? 0
						: usageIndex + 1,
				fullText = usages[newIndex],
				newDecreasing =
					text === "" ? false : text === fullText ? true : decreasing,
				newLength = newDecreasing ? text.length - 1 : text.length + 1;

			const timeout = setTimeout(
				looper,
				newLength == 0
					? emptyPause
					: newLength === fullText.length
					? fullPause
					: typingPause
			);

			return {
				usageIndex: newIndex,
				decreasing: newDecreasing,
				text: fullText.substring(0, newLength),
				timeout,
			};
		});
	}, [current]);

	// Kick off the initial animation
	useEffect(() => {
		const timeout = setTimeout(looper, fullPause);
		setCurrent((current) => ({
			...current,
			timeout,
		}));
	}, []);

	// Separate effect to remove the timeout on unmount
	useEffect(() => {
		return () => {
			const { timeout } = current;
			if (timeout) clearTimeout(timeout);
		};
	}, [current]);

	const handleWatchDemo = useCallback(() => {
		const el = document.getElementById("demo");
		if (el) {
			el.scrollIntoView({ behavior: "smooth", block: "center" });
		}

		// Give the browser a moment to start scrolling, then trigger play.
		setTimeout(() => {
			const win = window as Window & {
				playDemoVideo?: () => Promise<void> | void;
			};
			if (typeof win.playDemoVideo === "function") {
				void win.playDemoVideo();
			}
		}, 400);
	}, []);

	return (
		<div className={styles.wrapper}>
			<Container className={styles.container}>
				<div className={styles.text}>
					<div className={styles.aiChip}>
						<AIIcon />
						AI-Powered Game Creation
					</div>
					<h1 className={styles.heading1}>
						Build your own
						<span className={styles.headingGradient}>Role Playing Games</span>
					</h1>
					<p className={styles.lead}>
						<span className={styles.leadText}>
							Transform your narrative into a role playing game with our award
							winning AI game builder
						</span>
					</p>
				</div>
				<div className={styles.actions}>
					<div className={styles.actionsWrapper}>
						<Button
							to="https://my.adeptly.ai/"
							style={{ marginRight: "1em" }}
							className={styles.cta}
						>
							Start building
						</Button>
						<Button
							variant="secondary"
							onClick={handleWatchDemo}
							className={styles.cta}
						>
							Watch demo
						</Button>
					</div>
				</div>
			</Container>
			<div className={styles.background}>
				<span className={styles.ball}></span>
				<span className={styles.ball}></span>
				<span className={styles.ball}></span>
				<span className={styles.ball}></span>
				<span className={styles.ball}></span>
				<span className={styles.ball}></span>
			</div>
		</div>
	);
};
